/**
 * 클라이언트 진입점 — 두 가지 모드를 지원한다.
 *
 *  · 멀티(net): villager-sim 권위 서버에 접속. 클라는 상태를 바꾸지 않고
 *    인텐트만 보내며, 스냅샷을 받아 그린다 (지시서 §2).
 *  · 싱글(single): 서버가 없으면 브라우저에서 sim-core를 직접 돌린다(M1~M3 동작).
 *
 * 지형/숲/마왕성 같은 정적 요소는 시드에서 결정적으로 생성되므로,
 * 멀티에서도 서버가 준 시드로 로컬 World를 만들어 "지형 생성기"로만 쓴다(step 호출 안 함).
 *
 * URL 옵션:  ?single           강제 싱글
 *            ?server=ws://...  서버 주소 지정
 */
import * as THREE from 'three';
import { ECONOMY, PARCEL_BUILDS, WORLD, type BuildId, type ParcelType } from '@villager/shared';
import { tileAt, World, type SeasonResult } from '@villager/sim-core';
import { createTerrain } from './terrain.js';
import { buildParcelInterior, rebuildParcelWorldMesh } from './parcels.js';
import { EntityLayer } from './entities.js';
import { OrbitCamera } from './camera.js';
import { TiltShift } from './postfx.js';
import { UI } from './ui.js';
import { defaultWsUrl, NetClient, type Snapshot } from './net.js';
import { ensureSession } from './auth.js';
import { ChroniclePanel } from './chronicle.js';

const params = new URLSearchParams(window.location.search);
const ui = new UI();

// ── 모드 결정: 서버에 붙어보고 안 되면 싱글 폴백 ──
let net: NetClient | null = null;
let seed = Math.floor(Math.random() * 0x7fffffff);

// 게스트 즉시 시작: 서버 토큰을 확보해두면 재접속/서버 재시작에도 같은 사람으로 이어진다
const session = params.has('single') ? null : await ensureSession();

if (!params.has('single')) {
  const c = new NetClient(params.get('server') ?? defaultWsUrl());
  if (await c.connect()) {
    net = c;
    seed = c.seed;
  }
}
const online = net !== null;
ui.setMode(online ? '멀티' : '싱글', online);

// 연대기 열람 패널 (기록의 상호 링크를 걸어다닐 수 있게)
const chronicle = new ChroniclePanel();
chronicle.setToken(session?.token ?? null);

/**
 * 멀티에서는 이 World를 "지형 생성 + 서버 상태 미러"로만 쓴다(step 호출 금지).
 * 싱글에서는 이 World가 곧 게임이다.
 */
const w = new World(seed);

// ── three 기본 ──
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x9fb6c9);
scene.fog = new THREE.Fog(0x9fb6c9, 260, 560);
const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
document.body.appendChild(renderer.domElement);
const camera = new THREE.PerspectiveCamera(38, window.innerWidth / window.innerHeight, 1, 1000);
scene.add(new THREE.AmbientLight(0xbcc8d8, 0.55));
const sun = new THREE.DirectionalLight(0xfff2dc, 1.1);
sun.position.set(60, 120, 50);
scene.add(sun);

const terrain = createTerrain(w);
scene.add(terrain.worldGroup);
const parcelScene = new THREE.Group();
parcelScene.visible = false;
scene.add(parcelScene);
const entities = new EntityLayer(terrain.worldGroup);
const parcelMeshes = new Map<string, THREE.Group>();

const view: { mode: 'world' | 'parcel'; c: number; r: number } = { mode: 'world', c: 0, r: 0 };
const orbit = new OrbitCamera(renderer.domElement, () => view.mode);
const tilt = new TiltShift(renderer);
let cellMeshes: THREE.Mesh[] = [];
let overlayShown = false;

const WORLD_LABEL =
  '🗺 월드맵 — 남쪽은 왕국, 북쪽 잿빛 땅은 마왕령 · 왕국 타일을 클릭해 필지를 매입하라';

// ── 서버 상태 (멀티 전용) ──
let snap: Snapshot | null = null;
/** 매입 인텐트를 보낸 뒤, 서버가 승인하면 자동으로 들어가기 위한 대기 */
let pendingEnter: { c: number; r: number } | null = null;

function enterParcel(c: number, r: number): void {
  if (!w.grid[c]?.[r]?.parcel) return;
  view.mode = 'parcel';
  view.c = c;
  view.r = r;
  terrain.worldGroup.visible = false;
  parcelScene.visible = true;
  cellMeshes = buildParcelInterior(parcelScene, w, c, r);
  const p = w.grid[c]![r]!.parcel!;
  ui.setLabel(
    `${p.type === 'village' ? '🏘 내 마을' : '💀 내 던전'} — 필지 (${c},${r}) 내부 · 바닥 칸을 클릭해 건설`,
  );
  ui.showBack(true);
  orbit.resetParcel();
}

function exitParcel(): void {
  rebuildParcelWorldMesh(terrain.worldGroup, w, view.c, view.r, parcelMeshes);
  view.mode = 'world';
  terrain.worldGroup.visible = true;
  parcelScene.visible = false;
  ui.setLabel(WORLD_LABEL);
  ui.showBack(false);
}
ui.onBack(exitParcel);
ui.setLabel(WORLD_LABEL);

// ── 인텐트: 싱글은 로컬 적용, 멀티는 서버로 전송 ──
const act = {
  buy(c: number, r: number, type: ParcelType): void {
    if (net) {
      net.intent({ k: 'buy', c, r, type });
      pendingEnter = { c, r };
    } else if (w.buyParcel(c, r, type)) {
      rebuildParcelWorldMesh(terrain.worldGroup, w, c, r, parcelMeshes);
      enterParcel(c, r);
    }
  },
  build(c: number, r: number, i: number, j: number, b: BuildId): void {
    if (net) net.intent({ k: 'build', c, r, i, j, b });
    else {
      w.build(c, r, i, j, b);
      cellMeshes = buildParcelInterior(parcelScene, w, c, r);
    }
  },
  demolish(c: number, r: number, i: number, j: number): void {
    if (net) net.intent({ k: 'demolish', c, r, i, j });
    else {
      w.demolish(c, r, i, j);
      cellMeshes = buildParcelInterior(parcelScene, w, c, r);
    }
  },
  repair(c: number, r: number): void {
    if (net) net.intent({ k: 'repair', c, r });
    else {
      w.repair(c, r);
      rebuildParcelWorldMesh(terrain.worldGroup, w, c, r, parcelMeshes);
    }
  },
  clear(c: number, r: number): void {
    if (net) net.intent({ k: 'clear', c, r });
    else w.clearForest(c, r);
  },
};

// ── 클릭 (드래그와 구분: 6px 초과 이동 시 무시) ──
const ray = new THREE.Raycaster();
const mouse = new THREE.Vector2();
let downPos: [number, number] | null = null;
renderer.domElement.addEventListener('pointerdown', (e) => {
  downPos = [e.clientX, e.clientY];
});
renderer.domElement.addEventListener('pointerup', (e) => {
  if (!downPos) return;
  if (!net && w.seasonOver) return;
  const moved = Math.hypot(e.clientX - downPos[0], e.clientY - downPos[1]);
  downPos = null;
  if (moved > 6 || e.button !== 0) return;
  mouse.set((e.clientX / window.innerWidth) * 2 - 1, -(e.clientY / window.innerHeight) * 2 + 1);
  ray.setFromCamera(mouse, camera);
  if (view.mode === 'world') clickWorld(e.clientX, e.clientY);
  else clickParcel(e.clientX, e.clientY);
});

/** 이 필지가 내 것인가 (멀티: 서버 소유자, 싱글: 항상 내 것). */
function isMine(c: number, r: number): boolean {
  if (!net) return true;
  return snap?.parcels.find((p) => p.c === c && p.r === r)?.mine ?? false;
}

function clickWorld(sx: number, sy: number): void {
  const hit = ray.intersectObject(terrain.terrainMesh)[0];
  if (!hit) return;
  const { c, r } = tileAt(hit.point.x, hit.point.z);
  if (c < 0 || c >= WORLD.GW || r < 0 || r >= WORLD.GD) return;
  if (r < w.frontRow) {
    ui.pushFeed([{ id: Date.now(), msg: '그곳은 마왕령이다. 전선이 올라가야 개척할 수 있다.', red: true }]);
    return;
  }
  const t = w.grid[c]![r]!;
  if (t.parcel) {
    const mine = isMine(c, r);
    const paralyzed = t.parcel.paralyzed > 0;
    const btns: HTMLButtonElement[] = [];
    if (mine) {
      btns.push(
        ui.menuButton('🚪 들어가기', '', () => {
          ui.closeMenu();
          enterParcel(c, r);
        }),
      );
      if (paralyzed) {
        btns.push(
          ui.menuButton('긴급 수리', `${ECONOMY.REPAIR_COST}금`, () => {
            act.repair(c, r);
            ui.closeMenu();
          }),
        );
      }
    }
    btns.push(ui.menuButton('닫기', '', () => ui.closeMenu()));
    ui.openMenu(
      sx,
      sy,
      `${t.parcel.type === 'village' ? '🏘 마을' : '💀 던전'} 필지 (${c},${r})`,
      mine
        ? `${paralyzed ? "<b style='color:#ff8a6a'>습격 피해로 마비 중</b><br>" : ''}안으로 들어가 세부를 꾸밀 수 있다.`
        : '다른 마을사람의 필지다. 모험가들이 여기도 들른다.',
      btns,
    );
  } else if (t.forest) {
    ui.openMenu(sx, sy, `🌲 숲 타일 (${c},${r})`, '개간해야 필지로 쓸 수 있다.', [
      ui.menuButton('개간', `${ECONOMY.FOREST_CLEAR_COST}금`, () => {
        act.clear(c, r);
        ui.closeMenu();
      }),
      ui.menuButton('닫기', '', () => ui.closeMenu()),
    ]);
  } else {
    ui.openMenu(sx, sy, `빈 타일 (${c},${r})`, '필지를 매입하고 용도를 정한다.', [
      ui.menuButton('🏘 마을 필지로 매입', `${ECONOMY.PARCEL_COST}금`, () => {
        act.buy(c, r, 'village');
        ui.closeMenu();
      }),
      ui.menuButton('💀 던전 필지로 매입', `${ECONOMY.PARCEL_COST}금`, () => {
        act.buy(c, r, 'dungeon');
        ui.closeMenu();
      }),
      ui.menuButton('닫기', '', () => ui.closeMenu()),
    ]);
  }
}

function clickParcel(sx: number, sy: number): void {
  const hit = ray.intersectObjects(cellMeshes)[0];
  if (!hit) return;
  const { i, j } = hit.object.userData as { i: number; j: number };
  const p = w.grid[view.c]![view.r]!.parcel!;
  const cell = p.cells[i]![j]!;
  if (!isMine(view.c, view.r)) {
    ui.openMenu(sx, sy, '남의 필지', '여기는 다른 마을사람의 땅이다.', [
      ui.menuButton('닫기', '', () => ui.closeMenu()),
    ]);
    return;
  }
  if (!cell.b) {
    const defs = PARCEL_BUILDS[p.type];
    const btns = (Object.keys(defs) as BuildId[]).map((k) => {
      const d = defs[k];
      return ui.menuButton(`${d.icon} ${d.name}`, `${d.cost}금`, () => {
        act.build(view.c, view.r, i, j, k);
        ui.closeMenu();
      });
    });
    btns.push(ui.menuButton('닫기', '', () => ui.closeMenu()));
    ui.openMenu(sx, sy, `빈 칸 (${i},${j}) — 무엇을 지을까?`, '', btns);
  } else {
    const d = PARCEL_BUILDS[p.type][cell.b];
    ui.openMenu(sx, sy, `${d.icon} ${d.name}`, d.stockMax ? `재고 ${cell.stock}/${d.stockMax}` : '가동 중', [
      ui.menuButton('철거', '환불 없음', () => {
        act.demolish(view.c, view.r, i, j);
        ui.closeMenu();
      }),
      ui.menuButton('닫기', '', () => ui.closeMenu()),
    ]);
  }
}

// ── 서버 스냅샷 → 로컬 미러 반영 ──
if (net) {
  net.hello(session?.name ?? '마을사람', session?.token);
  net.onFeed = (msgs) => ui.pushFeed(msgs);
  net.onSeason = (result) => {
    if (!overlayShown) {
      overlayShown = true;
      ui.showOverlay(result as SeasonResult);
    }
  };
  net.onClose = () => ui.setMode('연결 끊김', false);
  net.onSnapshot = (s) => {
    snap = s;
    // 전선
    if (w.frontRow !== s.world.frontRow) {
      w.frontRow = s.world.frontRow;
      terrain.rebuildFlags(w.frontRow);
    }
    // 필지 미러링 (기존 렌더러를 그대로 재사용하기 위해 grid에 써넣는다)
    const seen = new Set<string>();
    for (const pv of s.parcels) {
      seen.add(`${pv.c},${pv.r}`);
      const tile = w.grid[pv.c]![pv.r]!;
      let changed = false;
      if (!tile.parcel) {
        tile.parcel = {
          type: pv.type,
          paralyzed: pv.paralyzed,
          owner: pv.owner,
          cells: Array.from({ length: WORLD.IN }, () =>
            Array.from({ length: WORLD.IN }, () => ({ b: null, stock: 0, prodT: 0 })),
          ),
        };
        tile.forest = false;
        changed = true;
      }
      const parcel = tile.parcel;
      if (parcel.paralyzed !== pv.paralyzed) {
        parcel.paralyzed = pv.paralyzed;
        changed = true;
      }
      parcel.owner = pv.owner;
      for (let i = 0; i < WORLD.IN; i++) {
        for (let j = 0; j < WORLD.IN; j++) {
          const want = pv.cells[i * WORLD.IN + j] ?? null;
          const cell = parcel.cells[i]![j]!;
          if (cell.b !== want) {
            cell.b = want;
            changed = true;
          }
        }
      }
      if (changed) {
        rebuildParcelWorldMesh(terrain.worldGroup, w, pv.c, pv.r, parcelMeshes);
        if (view.mode === 'parcel' && view.c === pv.c && view.r === pv.r) {
          cellMeshes = buildParcelInterior(parcelScene, w, pv.c, pv.r);
        }
      }
      // 매입 승인되면 자동 입장
      if (pendingEnter && pendingEnter.c === pv.c && pendingEnter.r === pv.r && pv.mine) {
        pendingEnter = null;
        enterParcel(pv.c, pv.r);
      }
    }
    // 서버에서 사라진 필지는 미러에서도 제거
    for (let c = 0; c < WORLD.GW; c++) {
      for (let r = 0; r < WORLD.GD; r++) {
        if (w.grid[c]![r]!.parcel && !seen.has(`${c},${r}`)) {
          w.grid[c]![r]!.parcel = null;
          rebuildParcelWorldMesh(terrain.worldGroup, w, c, r, parcelMeshes);
        }
      }
    }
  };
}

// ── 리사이즈 ──
window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  tilt.setSize();
});

// ── 루프 ──
const clock = new THREE.Clock();
let viewportT = 0;

function frame(): void {
  const dt = Math.min(0.05, clock.getDelta());
  const t = clock.elapsedTime;

  if (net) {
    // 뷰포트 부분 구독 갱신 (0.5초마다)
    viewportT += dt;
    if (viewportT > 0.5) {
      viewportT = 0;
      const cam = orbit.camW;
      net.viewport(cam.tx, cam.tz, Math.max(150, cam.dist * 1.8));
    }
    if (snap) {
      entities.syncNet(snap.ents, dt, t, terrain.paintPath);
      ui.refreshHUDNet(snap.you, snap.world);
      ui.renderRaidWarningAt(snap.world.raidWarning);
    }
  } else {
    if (!w.seasonOver) w.step(dt);
    if (w.dirtyParcels.size) {
      for (const key of w.dirtyParcels) {
        const [c, r] = key.split(',').map(Number) as [number, number];
        rebuildParcelWorldMesh(terrain.worldGroup, w, c, r, parcelMeshes);
      }
      w.dirtyParcels.clear();
    }
    if (w.frontDirty) {
      terrain.rebuildFlags(w.frontRow);
      w.frontDirty = false;
    }
    entities.sync(w, t, terrain.paintPath);
    ui.refreshHUD(w);
    ui.renderFeed(w);
    ui.renderRaidWarning(w);
    if (w.seasonOver && w.seasonResult && !overlayShown) {
      overlayShown = true;
      ui.showOverlay(w.seasonResult);
    }
  }

  orbit.apply(camera);
  tilt.render(scene, camera);
  requestAnimationFrame(frame);
}

if (!net) {
  w.feed('당신은 마을사람이다. 왕국 타일을 클릭해 첫 필지를 매입하라 — 모험가들이 오고 있다.');
} else {
  ui.pushFeed([
    { id: -1, msg: '세계에 접속했다. 다른 마을사람들과 같은 땅을 나눠 쓴다.', red: false },
  ]);
}
frame();
